import isEqual from 'lodash/isEqual';
import { BooleanAttribute, getAttributesToSerialize } from '../core/attributes';
import { TimeAwareBase } from '../core/TimeAwareBase';
import { createValidator, retry } from '../libs/retry';
import {
    assertIsValidAudio,
    assertMinimum,
    assertNumber,
    assertNumberBetween,
    assertResponseOk,
    assertString
} from '../validators';
import { getGlobalAssetNetworkManager } from '../core/AssetNetworkManager';
import type {
    BufferingMode,
    LicenseEnum
} from '../enums';
import type Luminary from '../';
import type { Asset } from '../types';
import type { SceneGroup } from '../core/SceneGroup';
import type { Slide } from '../core/slide/Slide';
import type { Subslide } from '../core/slide/Subslide';
import { Config } from '../video-data/config';
import { Volumes } from './types';

export type AudioSourceData = {
    mp3_url: string;
    duration: number;
    source: number;
    source_id: string;
    license: LicenseEnum;
    attribution: string;
    description: string;
    tags: Array<string>;
    title: string;
    name: string;
    quality: number;
    uuid?: string;
    id?: number;
    ai_voice?: string;
};

enum SOURCE_TYPE {
    SOURCE_DATA = 'SOURCE_DATA',
    HTML_MEDIA_ELEMENT = 'HTML_MEDIA_ELEMENT',
    URL = 'URL',
    EMPTY = 'EMPTY'
}

/**
 * The AudioAsset can be used two different ways:
 *      1. pass it a <video/> tag (or any HTMLMediaElement)
 *          - the AudioAsset creates and manages a MediaElementAudioSourceNode
 *          - in this mode, playback interaction (play/pause/seek/etc) is controlled externally, not by the AudioAsset
 *          - the AudioAsset will control volume via the #gainNode
 *      2. pass in sourceData or a URL
 *          - the AudioAsset creates and manages an AudioBufferSourceNode
 *          - in this mode, the AudioAsset controls volume as well as playback interaction
 */
export class AudioAsset extends TimeAwareBase implements Asset {
    override _classType = 'AudioAsset';

    @BooleanAttribute()
    loop = false;

    #muted = false;

    #transitionPoints = [];

    // properties only used to control playback interaction by the AudioBufferSourceNode
    #seek = 0; // the value in seconds that we have seek'd to
    #startTime: number = 0;
    #endTime: number = null;
    #startDelay = 0;

    #volume = Volumes.FULL_VOLUME;

    #isLoaded = false;
    #failedToLoad = false;

    #url: string = undefined;
    #buffer: AudioBuffer = undefined;
    #htmlElement: HTMLMediaElement = undefined;
    #bufferSourceNode: AudioBufferSourceNode = undefined;
    #mediaElementAudioSourceNode: MediaElementAudioSourceNode = undefined;
    #gainNode: GainNode = undefined;
    #loadingSourceUrl: string = undefined;
    #sentCanPlay: boolean = false;

    #sourceType = SOURCE_TYPE.EMPTY;

    // the data from sourceData
    #mp3Url: string = undefined;
    #duration: number = undefined;
    #source: number = undefined;
    #sourceId: string = undefined;
    #license: LicenseEnum = undefined;
    #attribution: string = undefined;
    #description: string = undefined;
    #tags: Array<string> = undefined;
    #title: string = undefined;
    #name: string = undefined;
    #quality: number = undefined;
    #sourceDataId: number = undefined;
    #sourceDataUuid: string = undefined;
    #ai_voice: string = undefined;


    override destroy() {
        this.stop();

        super.destroy();
        if (this.#gainNode) {
            this.#gainNode.disconnect();
            this.#gainNode = undefined;
        }
        if (this.#bufferSourceNode) {
            this.#bufferSourceNode.disconnect();
            this.#bufferSourceNode = undefined;
        }
        if (this.#mediaElementAudioSourceNode) {
            this.#mediaElementAudioSourceNode.disconnect();
            this.#mediaElementAudioSourceNode = undefined;
        }
    }

    get controlsPlayback() {
        return (this.#sourceType === SOURCE_TYPE.URL || this.#sourceType === SOURCE_TYPE.SOURCE_DATA);
    }

    get failedToLoad() {
        return this.#failedToLoad;
    }

    get isLoaded() {
        if (this.#sourceType === SOURCE_TYPE.HTML_MEDIA_ELEMENT) {
            return this.#htmlElement && this.#htmlElement.duration !== undefined;
        }
        return this.#isLoaded || !this.shouldLoad;
    }

    get isDoneTryingToLoad() {
        return this.isLoaded || this.failedToLoad;
    }

    get muted() {
        return this.#muted;
    }

    set muted(muted) {
        this.#muted = muted;
    }

    set transitionPoints(transitionPoints) {
        this.#transitionPoints = transitionPoints;
    }

    get transitionPoints() {
        return this.#transitionPoints;
    }

    /**
     * @deprecated use sourceData instead
     */
    get source(): AudioSourceData {
        return this.sourceData;
    }

    /**
     * @deprecated use sourceData instead
     */
    set source(data: AudioSourceData) {
        this.sourceData = data;
    }

    get sourceData(): AudioSourceData {
        if (this.#sourceType === SOURCE_TYPE.EMPTY) {
            return null;
        }
        return {
            mp3_url: this.#mp3Url,
            duration: this.#duration,
            source: this.#source,
            source_id: this.#sourceId,
            license: this.#license,
            attribution: this.#attribution,
            description: this.#description,
            tags: this.#tags,
            title: this.#title,
            name: this.#name,
            quality: this.#quality,
            uuid: this.#sourceDataUuid,
            id: this.#sourceDataId,
            ai_voice: this.#ai_voice,
        };
    }
    set sourceData(data: AudioSourceData) {
        if (isEqual(this.sourceData, data)) {
            return;
        }
        const sourceData = data as AudioSourceData;

        assertIsValidAudio(sourceData, true);

        this.#startTime = 0;
        this.#endTime = null;

        this.#mp3Url = sourceData.mp3_url;
        this.#duration = sourceData.duration;
        this.#source = sourceData.source;
        this.#sourceId = sourceData.source_id;
        this.#license = sourceData.license;
        this.#attribution = sourceData.attribution;
        this.#description = sourceData.description;
        this.#tags = sourceData.tags;
        this.#title = sourceData.title;
        this.#name = sourceData.name;
        this.#quality = sourceData.quality;
        this.#sourceDataId = sourceData.id;
        this.#sourceDataUuid = sourceData.uuid;
        this.#ai_voice = sourceData.ai_voice;

        // set isLoaded and sourceType
        this.#isLoaded = false;
        this.#sourceType = SOURCE_TYPE.SOURCE_DATA;

        // clear the other "source" properties
        this.#url = undefined;
        this.#htmlElement = undefined;

        this.requiresUpdate = true;
    }

    /**
     * @internal
     */
    get url() {
        return this.#url;
    }

    /**
     * @internal
     */
    set url(url: string) {
        if (this.#url === url) {
            return;
        }
        assertString(url);
        this.#url = url;
        this.#startTime = 0;
        this.#endTime = null;

        // set isLoaded and sourceType
        this.#sourceType = SOURCE_TYPE.URL;
        this.#isLoaded = false;

        // clear the other "source" properties
        this.#mp3Url = undefined;
        this.#duration = undefined;
        this.#source = undefined;
        this.#sourceId = undefined;
        this.#license = undefined;
        this.#attribution = undefined;
        this.#description = undefined;
        this.#tags = undefined;
        this.#title = undefined;
        this.#name = undefined;
        this.#quality = undefined;
        this.#sourceDataId = undefined;
        this.#htmlElement = undefined;
        this.#sourceDataUuid = undefined;
        this.#ai_voice = undefined;

        this.requiresUpdate = true;
    }

    /**
     * @internal
     */
    get mediaElement() {
        return this.#htmlElement;
    }

    /**
     * @internal
     */
    set mediaElement(mediaElement: HTMLMediaElement) {
        if (this.#htmlElement === mediaElement) {
            return;
        }
        this.#htmlElement = mediaElement;
        this.#startTime = 0;
        this.#endTime = null;

        // set isLoaded and sourceType
        this.#isLoaded = true; // in this case, we are using a <video/> tag, and loading is handled by the MediaAsset.
        this.#sourceType = SOURCE_TYPE.HTML_MEDIA_ELEMENT;

        // clear the other "source" properties
        this.#mp3Url = undefined;
        this.#duration = undefined;
        this.#source = undefined;
        this.#sourceId = undefined;
        this.#license = undefined;
        this.#attribution = undefined;
        this.#description = undefined;
        this.#tags = undefined;
        this.#title = undefined;
        this.#name = undefined;
        this.#quality = undefined;
        this.#sourceDataId = undefined;
        this.#sourceDataUuid = undefined;
        this.#url = undefined;
        this.#ai_voice = undefined;

        this.requiresUpdate = true;
    }

    override _propagatePlay() {
        this.#updateHTMLMuted();
        if (!this.isLoaded || !this.shouldLoad) {
            return;
        }

        this._constructAudioGraph();
        this._buildGainCurve();

        if (!this.controlsPlayback) {
            return;
        }

        // you can only play an AudioBufferSourceNode one time. The official recommendation is that
        // the audio graph is cheap to construct, and should be re-built on every call to play:
        // https://developer.mozilla.org/en-US/docs/Web/API/AudioBufferSourceNode
        this._schedulePlayback();
    }

    override _propagatePause() {
        if (!this.isLoaded) {
            return;
        }

        if (!this.controlsPlayback) {
            return;
        }

        // you can only play an AudioBufferSourceNode one time. So we need to stop playback immediately.
        // if/when we call play() again, we will re-create the bufferSourceNode and re-setup the correct
        // playback state
        if (this.#bufferSourceNode) {
            this.#bufferSourceNode.stop();
        }
    }

    stop() {
        // since we don't keep track of the "currentTime" in the AudioAsset
        // stop is no different than pause!
        this._propagatePause();
    }

    /**
     * The startDelay is an amount of time (in ms) that the audio asset should wait before playing.
     * This is useful for situations when you want to be able to schedule playback to begin at
     * some specific time in the future. For example, when we render the audio, we want to schedule scene-based
     * audio to start playing when the scene first appears on the scene.
     *
     * This property is not serialized, it's only used to schedule playback.
     */
    get startDelay() {
        return this.#startDelay;
    }

    set startDelay(startDelay) {
        assertNumberBetween(startDelay, -Infinity, Infinity);
        this.#startDelay = startDelay;
    }

    /**
     * The startTime is a number of milliseconds that represents an offset into the audio asset
     * from which the audio asset should always play. For example, if we have an audio asset, but
     * we wish to skip the first 5 seconds of the track when we tell it to play, we would set
     * the startTime to 5000.
     */
    get startTime(): number {
        return this.#startTime;
    }

    set startTime(startTime: number) {
        if (this.#startTime === startTime) {
            return;
        }
        assertNumberBetween(startTime, 0, Infinity);
        this.#startTime = startTime;
        this.requiresUpdate = true;
    }

    /**
     * The endTime is in milliseconds. It represents the time at which the audio asset should stop playing.
     */
    get endTime(): number {
        if (this.#endTime === null) {
            return (this.duration * 1000.0);
        }
        return this.#endTime;
    }

    set endTime(endTime: number) {
        if (endTime === this.#endTime) {
            return;
        }
        assertNumber(endTime, true);
        assertMinimum(endTime, 0, true);
        this.#endTime = endTime;
        this.requiresUpdate = true;
    }

    get volume() {
        return this.#volume;
    }

    set volume(newVolume: number) {
        if (newVolume === this.#volume) {
            return;
        }
        assertNumberBetween(newVolume, Volumes.SILENT, Volumes.FULL_VOLUME);
        this.#volume = newVolume;
        this.requiresUpdate = true;
    }

    /**
     * This is the duration of the audio file. The AudioAsset may actually play for:
     *  - longer than the duration (if it loops)
     *  - shorter than the duration (if only part of it plays)
     */
    get duration() {
        return this.#duration;
    }

    _bufferAtTime(time: number, _mode: BufferingMode) {
        this.#seek = time;
    }

    override _update() {
        void this.loadSource();
    }

    #transitionPointIsInPresentOrFuture(transitionPoint) {
        return this.#getTransitionPointTimeFromNow(transitionPoint) >= 0;
    }

    #getTransitionPointTimeFromNow(transitionPoint) {
        return this.#startDelay + transitionPoint.time - this.#seek;
    }

    /**
     * The gain node controls the volume (and mute) of the audio. However, to enable automatic playback
     * of muted videos, we need to make sure the muted property of the HTMLMediaElement is set to true.
     * Otherwise, if the user hasn't interacted with the page, the video won't play.
     * https://developer.chrome.com/blog/autoplay/#new-behaviors
     */
    #updateHTMLMuted() {
        if (!this.#htmlElement) {
            return;
        }
        const muted = this.#muted || this.root.muted || this.#transitionPoints.length === 0 || this.volume === Volumes.SILENT;
        this.#htmlElement.muted = muted;
    }

    _buildGainCurve() {
        // cancel all curves going forward
        this.#gainNode.gain.cancelScheduledValues(this.root.audioContext.currentTime);
        if (this.#muted || this.root.muted || this.#transitionPoints.length === 0) {
            return;
        }

        let firstFuturePointIndex = 0;
        for (let i = 0; i < this.#transitionPoints.length; i++) {
            const currentPoint = this.#transitionPoints[i];
            if (this.#transitionPointIsInPresentOrFuture(currentPoint)) {
                // we found the first point that is actually in the future!
                // Store it and break.
                firstFuturePointIndex = i;
                break;
            }
        }
        if (firstFuturePointIndex > 0) {
            // we are skipping some points in the beginning, so we need to do some linear
            // interpolation to get the value at the beginning of the first transition
            const firstFuturePoint = this.#transitionPoints[firstFuturePointIndex];
            const lastHistoricPoint = this.#transitionPoints[firstFuturePointIndex - 1];

            // linear interpolation: https://matthew-brett.github.io/teaching/linear_interpolation.html
            const y0 = firstFuturePoint.volume;
            const y1 = lastHistoricPoint.volume;
            const x0 = this.#getTransitionPointTimeFromNow(firstFuturePoint);
            const x1 = this.#getTransitionPointTimeFromNow(lastHistoricPoint);
            const x = 0; // interpolating for time zero
            const y = y0 + (x - x0) * (y1 - y0) / (x1 - x0);

            this.#gainNode.gain.linearRampToValueAtTime(
                y / 100.0,
                this.root.audioContext.currentTime // setting the volume at exactly the currentTime (time zero)
            );
        }
        else {
            // check if ALL the transition points are in the past. If so,
            // use the last point as the current volume
            if (this.transitionPoints.length > 0) {
                let lastPoint = this.transitionPoints[0];
                this.#transitionPoints.forEach((transitionPoint) => {
                    if (transitionPoint.time > lastPoint.time) {
                        lastPoint = transitionPoint;
                    }
                });
                if (this.#getTransitionPointTimeFromNow(lastPoint) < 0) {
                    this.#gainNode.gain.linearRampToValueAtTime(
                        lastPoint.volume / 100.0,
                        this.root.audioContext.currentTime // setting the volume at exactly the currentTime (time zero)
                    );
                }
            }
        }

        // now the simple part - just add the future points to the curve!
        for (let i = firstFuturePointIndex; i < this.#transitionPoints.length; i++) {
            const currentPoint = this.#transitionPoints[i];
            if (this.#transitionPointIsInPresentOrFuture(currentPoint)) {
                this.#gainNode.gain.linearRampToValueAtTime(
                    currentPoint.volume / 100.0,
                    this.root.audioContext.currentTime + (this.#getTransitionPointTimeFromNow(currentPoint) / 1000.0)
                );
            }
        }
    }

    _schedulePlayback() {
        // only the AudioBufferSourceNode is controlled by the AudioAsset.
        // if the audio is coming off of an htmlMediaElement, playback is controlled by the MediaAsset
        if (!this.controlsPlayback) {
            return;
        }

        // set up looping
        this.#bufferSourceNode.loop = this.loop;
        this.#bufferSourceNode.loopStart = this.#startTime / 1000.0;
        this.#bufferSourceNode.loopEnd = this.endTime / 1000.0;

        // now schedule the exact times that the node should play using start()
        const now = this.root.audioContext.currentTime;

        let startDelay = this.#startDelay;
        let seek = this.#seek;
        if (startDelay < 0) {
            // if the startDelay is negative, that means the track
            // should have started in the past. That's the same thing
            // as seeking into the future!
            seek = seek - startDelay;
            startDelay = 0;
        }

        const when = now + (startDelay / 1000.0);
        const offset = (this.#startTime + seek) / 1000.0;
        this.#bufferSourceNode.start(when, offset); // docs are here: https://developer.mozilla.org/en-US/docs/Web/API/AudioBufferSourceNode/start
    }

    #createGainNode() {
        if (this.#gainNode) {
            this.#gainNode.disconnect();
        }
        return this.root.audioContext.createGain();
    }

    _constructAudioGraph() {
        if (this.#sourceType === SOURCE_TYPE.HTML_MEDIA_ELEMENT && this.#mediaElementAudioSourceNode === undefined) {
            if (!(this.root.audioContext instanceof AudioContext)) {
                throw new Error('media element source type is only supported with AudioContext.');
            }

            this.#mediaElementAudioSourceNode = this.root.audioContext.createMediaElementSource(this.#htmlElement as HTMLMediaElement);
            this.#gainNode = this.#createGainNode();
            this.#mediaElementAudioSourceNode.connect(this.#gainNode);
            this.#gainNode.connect(this.root.audioContext.destination);
        }
        else if (this.#sourceType === SOURCE_TYPE.URL || this.#sourceType === SOURCE_TYPE.SOURCE_DATA) {
            if (this.#bufferSourceNode) {
                this.#bufferSourceNode.disconnect();
            }
            this.#bufferSourceNode = this.root.audioContext.createBufferSource();
            this.#bufferSourceNode.buffer = this.#buffer;
            this.#gainNode = this.#createGainNode();
            this.#bufferSourceNode.connect(this.#gainNode);
            this.#gainNode.connect(this.root.audioContext.destination);
        }
        // By default, mute the audio.
        // If it needs to audible, the audio animation curves will set the volume.
        this.#gainNode.gain.value = 0;
    }

    onLoaded() {
        this.requiresUpdate = true;
        this._emitAssetCanPlay();
    }

    _emitAssetCanPlay() {
        if (!this.#sentCanPlay) {
            this.#sentCanPlay = true;
            this.emit('assetCanPlay');
        }
    }

    /**
     * Because AudioAsset extends TimeAwareBase, we need to implement _emitAssetsCanPlay, but
     * this is a single asset so we emit assetCanPlay. Conceptually, this should be equivalent to
     * _emitAssetCanPlay in BaseAsset. This bubbles the assetCanPlay event from onLoaded.
     */
    _emitAssetsCanPlay() {
        if (this._luminaryParent) {
            this._luminaryParent.emit('assetCanPlay');
        }
    }

    get _luminaryParent(): Luminary | SceneGroup | Slide | Subslide {
        // currently parent is either Luminary or MediaAsset or SceneGroup
        if (Config.isAsset(this.parent) && Config.isMediaAsset(this.parent)) {
            return (this.parent)._luminaryParent;
        }

        if (this.parent === this.root) {
            return this.root;
        }

        return this.parent as SceneGroup;
    }

    isLoadingUrl(url: string): boolean {
        return !!this.#loadingSourceUrl && this.#loadingSourceUrl === url;
    }

    get shouldLoad(): boolean {
        if (this.#volume === Volumes.SILENT) {
            return false;
        }
        if (this.#sourceType === SOURCE_TYPE.EMPTY) {
            return false;
        }
        if (typeof this.fetchResources === 'boolean') {
            return this.fetchResources;
        }
        return this.fetchResources && this.fetchResources.audio && this.sourceData !== null;
    }

    get canPlay() {
        return this.isDoneTryingToLoad;
    }

    static #cache: Map<string, AudioBuffer | Promise<AudioBuffer>> = new Map();

    static clearCache() {
        AudioAsset.#cache.clear();
    }

    async loadSource() {
        if (this.isDoneTryingToLoad || this.#sourceType === SOURCE_TYPE.HTML_MEDIA_ELEMENT) {
            return;
        }

        if (this.#sourceType === SOURCE_TYPE.URL || this.#sourceType === SOURCE_TYPE.SOURCE_DATA) {
            const urlToLoad = this.#url || this.#mp3Url;
            let cacheEntry = AudioAsset.#cache.get(urlToLoad);

            if (cacheEntry) {
                if (cacheEntry instanceof Promise) {
                    cacheEntry = await cacheEntry;
                }
                this.#buffer = cacheEntry;
                this.#duration = this.#buffer.duration;
                this.#isLoaded = true;
                this.#loadingSourceUrl = undefined;
                this.onLoaded();
                return;
            }

            if (this.isLoadingUrl(urlToLoad)) {
                // we've already started loading this url, just wait!
                return;
            }
            this.#sentCanPlay = false;
            this.#loadingSourceUrl = urlToLoad;
            const assetNetworkManager = getGlobalAssetNetworkManager();
            assetNetworkManager.waitForNetworkSlot(this, requestFinished => {
                const validator = createValidator(assertResponseOk);
                const fetchAndDecodeAudio = async() => {
                    const response = await retry(5, validator(fetch), this.#loadingSourceUrl);
                    const arrayBuffer = await response.arrayBuffer();
                    return this.root.audioContext.decodeAudioData(arrayBuffer);
                };
                const promise = fetchAndDecodeAudio();
                AudioAsset.#cache.set(urlToLoad, promise);
                promise.then(buffer => {
                    if (this.#loadingSourceUrl !== urlToLoad) {
                        return;
                    }
                    this.#buffer = buffer;
                    this.#duration = this.#buffer.duration; // update the duration value
                    this.#isLoaded = true;
                    AudioAsset.#cache.set(urlToLoad, buffer);
                }).catch(err => {
                    if (this.#loadingSourceUrl !== urlToLoad) {
                        return;
                    }
                    console.warn(err);
                    this.#failedToLoad = true;
                }).finally(() => {
                    requestFinished();
                    if (this.#loadingSourceUrl !== urlToLoad) {
                        return;
                    }
                    this.#loadingSourceUrl = undefined;
                    this.onLoaded();
                });
            });
        }
    }

    _checkAndFixEndTime() {
        if (!this.isLoaded) {
            return;
        }

        // If the end time is longer than the source duration, set the
        // end time to null (ie. default value, no end point)
        if (this.#endTime > this.#duration * 1000.0) {
            this.#endTime = null;
        }
    }

    override _checkAndFixProperties() {
        super._checkAndFixProperties();

        this._checkAndFixEndTime();
    }

    override get propertiesToSerialize(): string[] {
        return super.propertiesToSerialize.concat([
            'sourceData',
            'id',
            'startTime',
            'endTime',
        ]).concat(getAttributesToSerialize(this));
    }

    override get editableProperties() {
        return super.editableProperties.concat([
            'volume',
        ]);
    }

    override get readonlyProperties(): any[] {
        return super.readonlyProperties.concat([
            'id',
        ]);
    }

    _sourceDataCustomSave() {
        if (this.#sourceType !== SOURCE_TYPE.SOURCE_DATA && this.#sourceType !== SOURCE_TYPE.EMPTY) {
            throw Error('you can only serialize AudioAssets that have source set with SOURCE_DATA');
        }
        return this.source;
    }

    _endTimeCustomSave() {
        // The getter returns the source duration when null.
        // But we want to serialize as a null value.
        return this.#endTime;
    }

    /**
     * __sourceNode is only intended to be used by tests.
     * @internal
     */
    get __sourceNode() {
        return this.#bufferSourceNode || this.#mediaElementAudioSourceNode;
    }

    /**
     * __gainNode is only intended to be used by tests.
     * @internal
     */
    get __gainNode() {
        return this.#gainNode;
    }

    // ------------------------------------------------------------------------------------
    // TODO Because AudioAsset extends Base, it needs to implement the abstract methods
    //  However AudioAsset should be viewed & handled more like a BaseAsset because it's not
    //  part of luminary's core structure, but rather can be added or removed dynamically.
    //  The following are no-op implementations to satisfy the typings, but eventually
    //  should be refactored.
    // ------------------------------------------------------------------------------------

    _propagateBuffering(): never {
        throw new Error('should not be used');
    }

    get areAssetsDoneTryingToLoad(): never {
        throw new Error('should not be used');
    }
}
